import { useId, type ReactElement, type ReactNode } from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/** How long the pointer rests on a button before its tip shows (keyboard focus shows it at once). */
export const TIP_DELAY_MS = 400;

/**
 * The tip of the public onboarding page and the Leads Cockpit lives under one provider each (not global): a short delay,
 * and none between two buttons that are visited one after the other.
 */
export function TipProvider({ children }: { children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={TIP_DELAY_MS} skipDelayDuration={250}>
      {children}
    </TooltipProvider>
  );
}

/**
 * One short sentence for a button, the same everywhere: it shows after ~400 ms of hover and on keyboard focus (never on
 * hover alone), closes with Esc, and does not show on touch (Radix). It sits above dialogs and sheets (z 10200: the cockpit's
 * dialogs are at 10050/10055, popovers at 10100). The important information stays in the button's text or `aria-label`.
 *
 * A disabled button gets no pointer or focus events, so with `disabled` the tip sits on a wrapper that can be hovered
 * and focused (tabIndex 0) and describes the button by `aria-describedby`: the reason is said, not only shown.
 */
export function ButtonTip({
  label,
  children,
  side = "top",
  disabled = false,
}: {
  label: ReactNode;
  children: ReactElement;
  side?: "top" | "bottom" | "left" | "right";
  disabled?: boolean;
}) {
  const id = useId();
  if (!label) return children;
  // The wrapper is always there (so a button that turns on or off keeps its place in the tree and is not re-mounted); it only
  // takes a box and the focus while the button is disabled.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={disabled ? 0 : undefined}
          className={disabled ? "inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" : "contents"}
          aria-describedby={disabled ? id : undefined}
          data-tip-wrapper={disabled ? "true" : "false"}
        >
          {children}
          {disabled && (
            <span id={id} className="sr-only">
              {label}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={6} className="z-[10200] max-w-[240px]" data-testid="button-tip">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
